<template>
  <div class="panel engagement-budget" data-qid="engagement-budget" v-if="access.view_budget && budgetOn">
    <header class="panel__header">
      <div class="profile-compact">
        <div class="logo-container">
          <font-awesome-icon icon="hourglass-half" class="icon-svg--sm"/>
        </div>
        <div class="profile-compact__info">
          <div class="profile-compact__top">
            <div class="profile-compact__text">
              {{ $t('projects.engagement.budget.title') }}
            </div>
          </div>
        </div>
      </div>

      <div class="profile-compact__actions">
        <span class="icon-btn"
              v-if="canEditBudget"
              v-b-tooltip.hover
              :title="$t('projects.engagement.budget.edit')"
              @click="showEditModal"
              data-qid="engagement-budget-edit"
        >
          <font-awesome-icon icon="pen"/>
        </span>
        <span class="icon-btn"
              v-if="canLogTime"
              v-b-tooltip.hover
              :title="$t('projects.engagement.budget.log_time')"
              @click="showLogModal"
              data-qid="engagement-budget-log"
        >
          <font-awesome-icon icon="clock"/>
        </span>
      </div>
    </header>

    <div class="panel__body">
      <div class="engagement-budget__metrics">
        <div class="engagement-budget__metric">
          <div class="engagement-budget__metric-label">{{ $t('projects.engagement.budget.budget') }}</div>
          <div class="engagement-budget__metric-value">{{ $t('projects.engagement.budget.hours_value', { hours: hours(totalBudget) }) }}</div>
        </div>
        <div class="engagement-budget__metric">
          <div class="engagement-budget__metric-label">{{ $t('projects.engagement.budget.spent') }}</div>
          <div class="engagement-budget__metric-value">{{ $t('projects.engagement.budget.hours_value', { hours: hours(totalSpent) }) }}</div>
        </div>
        <div class="engagement-budget__metric">
          <div class="engagement-budget__metric-label">{{ $t('projects.engagement.budget.remaining') }}</div>
          <div class="engagement-budget__metric-value" :class="{'engagement-budget__over': totalBudget > 0 && totalSpent > totalBudget}">
            <span v-if="totalBudget > 0">{{ $t('projects.engagement.budget.hours_value', { hours: hours(totalBudget - totalSpent) }) }}</span>
            <span v-else>–</span>
          </div>
        </div>
      </div>

      <label class="mt-3">{{ $t('projects.engagement.budget.by_phase') }}</label>
      <div v-for="phase in phases" :key="'budget-phase-' + phase" class="engagement-budget__row">
        <span class="engagement-budget__row-label">{{ $t('projects.engagement.phase_short.' + phase) }}</span>
        <div class="engagement-budget__bar">
          <span :style="{ width: barWidth(phase) + '%' }"
                :class="{'engagement-budget__bar--over': isOver(phase), 'engagement-budget__bar--done': !isOver(phase) && spent(phase) > 0 && spent(phase) === budget(phase)}"
          ></span>
        </div>
        <span class="engagement-budget__row-value" :class="{'engagement-budget__over': isOver(phase)}">
          {{ hours(spent(phase)) }} / {{ $t('projects.engagement.budget.hours_value', { hours: hours(budget(phase)) }) }}
        </span>
      </div>

      <label class="mt-3">{{ $t('projects.engagement.budget.by_member') }}</label>
      <div v-for="row in spentByUser" :key="'budget-user-' + (row.user ? row.user.id : 0)" class="engagement-budget__row engagement-budget__row--member">
        <span class="engagement-budget__row-label text-truncate">
          <Avatar v-if="row.user"
                  :object="row.user"
                  size="xs"
                  class="mr-1"></Avatar>
          {{ row.user ? row.user.name : '' }}
        </span>
        <span class="engagement-budget__row-value">{{ $t('projects.engagement.budget.hours_value', { hours: hours(row.hours) }) }}</span>
      </div>
      <div v-if="!spentByUser.length" class="text-muted section-container">
        {{ $t('projects.engagement.budget.no_entries') }}
      </div>

      <div class="mt-3">
        <a href="#" @click.prevent="toggleEntries" data-qid="engagement-budget-entries-toggle">
          <font-awesome-icon :icon="showEntries ? 'chevron-up' : 'chevron-down'" class="mr-1"/>
          {{ $t('projects.engagement.budget.entries') }}
        </a>
      </div>
      <div v-if="showEntries" class="engagement-budget__entries" data-qid="engagement-budget-entries">
        <div v-for="entry in entries" :key="'entry-' + entry.id" class="engagement-budget__entry">
          <span class="engagement-budget__entry-date">{{ entry.entry_date | formatDate }}</span>
          <span class="engagement-budget__entry-user text-truncate">{{ entry.user ? entry.user.name : '' }}</span>
          <span class="engagement-budget__entry-phase text-truncate">
            {{ $t('projects.engagement.phase_short.' + entry.phase) }}<span v-if="entry.step"> · {{ $t('projects.engagement.step.' + entry.step) }}</span>
            <span v-if="entry.note" class="text-muted"> – {{ entry.note }}</span>
          </span>
          <span class="engagement-budget__entry-hours">{{ $t('projects.engagement.budget.hours_value', { hours: hours(entry.hours) }) }}</span>
          <span class="engagement-budget__entry-remove">
            <span v-if="canDeleteEntry(entry)"
                  class="cursor-pointer text-muted"
                  @click="deleteEntry(entry)"
                  v-b-tooltip.hover
                  :title="$t('projects.engagement.budget.delete_entry')">
              <font-awesome-icon icon="trash-alt"/>
            </span>
          </span>
        </div>
        <div v-if="!entries.length" class="text-muted">
          {{ $t('projects.engagement.budget.no_entries') }}
        </div>
      </div>
    </div>

    <!-- Edit budget modal -->
    <b-modal ref="edit-budget-modal" modal-class="task-modal">
      <template #modal-header>
        <div class="" style="width: 100%;">
          <span @click="$refs['edit-budget-modal'].hide()" class="btn btn-secondary cursor-pointer float-right">
            <font-awesome-icon icon="times" />
          </span>
          <h5 class="mb-1 text-center">{{ $t('projects.engagement.budget.edit') }}</h5>
        </div>
      </template>

      <b-alert v-if="error.length" show variant="danger">{{ error }}</b-alert>
      <p class="text-muted">{{ $t('projects.engagement.budget.edit_description') }}</p>
      <b-form-group v-for="phase in phases" :key="'budget-input-' + phase" :label="$t('projects.engagement.phase.' + phase)">
        <b-form-input type="number"
                      min="0"
                      step="0.5"
                      v-model="budgetForm[phase]"
                      :data-qid="'engagement-budget-input-' + phase"></b-form-input>
      </b-form-group>

      <template #modal-footer>
        <div class="" style="width: 100%;">
          <div class="alert alert-menu-color color-secondary mb-0" role="alert">
            <div class="text-right">
              <b-button variant="secondary" @click="$refs['edit-budget-modal'].hide()">{{ $t('projects.create.cancel') }}</b-button>
              <b-button class="ml-3"
                        variant="success"
                        :disabled="buttonDisabled"
                        @click="saveBudget"
                        data-qid="engagement-budget-save">
                {{ $t('projects.create.submit') }}
              </b-button>
            </div>
          </div>
        </div>
      </template>
    </b-modal>

    <!-- Log time modal -->
    <b-modal ref="log-time-modal" modal-class="task-modal">
      <template #modal-header>
        <div class="" style="width: 100%;">
          <span @click="$refs['log-time-modal'].hide()" class="btn btn-secondary cursor-pointer float-right">
            <font-awesome-icon icon="times" />
          </span>
          <h5 class="mb-1 text-center">{{ $t('projects.engagement.budget.log_time') }}</h5>
        </div>
      </template>

      <b-alert v-if="error.length" show variant="danger">{{ error }}</b-alert>
      <div class="row">
        <div class="col-sm-6">
          <b-form-group :label="$t('projects.engagement.budget.date')">
            <b-form-input type="date" v-model="logForm.entry_date" data-qid="engagement-log-date"></b-form-input>
          </b-form-group>
        </div>
        <div class="col-sm-6">
          <b-form-group :label="$t('projects.engagement.budget.hours')">
            <b-form-input type="number"
                          min="0.25"
                          max="24"
                          step="0.25"
                          v-model="logForm.hours"
                          data-qid="engagement-log-hours"></b-form-input>
          </b-form-group>
        </div>
      </div>
      <b-form-group :label="$t('projects.engagement.budget.phase')">
        <b-form-select v-model="logForm.phase"
                       :options="phaseOptions"
                       @change="logForm.step = null"
                       data-qid="engagement-log-phase"></b-form-select>
      </b-form-group>
      <b-form-group :label="$t('projects.engagement.budget.step')">
        <b-form-select v-model="logForm.step" :options="stepOptions" data-qid="engagement-log-step"></b-form-select>
      </b-form-group>
      <b-form-group :label="$t('projects.engagement.budget.note')">
        <b-form-textarea v-model="logForm.note" rows="2" data-qid="engagement-log-note"></b-form-textarea>
      </b-form-group>

      <template #modal-footer>
        <div class="" style="width: 100%;">
          <div class="alert alert-menu-color color-secondary mb-0" role="alert">
            <div class="text-right">
              <b-button variant="secondary" @click="$refs['log-time-modal'].hide()">{{ $t('projects.create.cancel') }}</b-button>
              <b-button class="ml-3"
                        variant="success"
                        :disabled="buttonDisabled"
                        @click="logTime"
                        data-qid="engagement-log-save">
                {{ $t('projects.engagement.budget.log_time') }}
              </b-button>
            </div>
          </div>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import piincHttp from '@/util/configureAxios.js';
import Avatar from '@/components/Avatar.vue';
import { PHASE_KEYS, PHASES, engagementPermissions, formatHours, budgetEnabled } from '@/views/Projects/Engagement/engagement.js';

export default {
  name: 'EngagementBudget',
  props: {
    project: {
      type: Object,
      required: true
    },
    engagement: {
      type: Object,
      required: true
    }
  },
  components: {
    Avatar
  },
  data: function() {
    return {
      phases: PHASE_KEYS,
      entries: [],
      showEntries: false,
      budgetForm: {},
      logForm: this.emptyLogForm(),
      buttonDisabled: false,
      error: ''
    }
  },
  computed: {
    // Budget and time registration can be switched off in Settings
    budgetOn() {
      return budgetEnabled(this.project);
    },
    access() {
      return engagementPermissions(this.project);
    },
    isOpen() {
      return this.project.status !== 'closed' && this.project.status !== 'completed';
    },
    canEditBudget() {
      return !!this.access.edit_budget && this.isOpen;
    },
    canLogTime() {
      return !!this.access.log_time && this.isOpen;
    },
    totalBudget() {
      return _.sum(_.map(this.phases, phase => this.budget(phase)));
    },
    totalSpent() {
      return _.sum(_.map(this.phases, phase => this.spent(phase)));
    },
    spentByUser() {
      return _.orderBy(this.engagement.spent_by_user || [], ['hours'], ['desc']);
    },
    phaseOptions() {
      return _.map(this.phases, phase => ({ value: phase, text: this.$t('projects.engagement.phase.' + phase) }));
    },
    stepOptions() {
      const steps = this.logForm.phase ? PHASES[this.logForm.phase] : [];
      return [{ value: null, text: '–' }].concat(_.map(steps, step => ({ value: step, text: this.$t('projects.engagement.step.' + step) })));
    },
    currentUser() {
      return this.$store.state.user;
    }
  },
  methods: {
    emptyLogForm() {
      return {
        entry_date: new Date().toISOString().slice(0, 10),
        hours: '',
        phase: 'acceptance',
        step: null,
        note: ''
      };
    },
    hours(value) {
      return formatHours(value);
    },
    budget(phase) {
      return this.engagement.budget ? Number(this.engagement.budget[phase]) || 0 : 0;
    },
    spent(phase) {
      return this.engagement.spent ? Number(this.engagement.spent[phase]) || 0 : 0;
    },
    isOver(phase) {
      return this.budget(phase) > 0 && this.spent(phase) > this.budget(phase);
    },
    barWidth(phase) {
      const budget = this.budget(phase);
      if (!budget) {
        return this.spent(phase) > 0 ? 100 : 0;
      }
      return Math.min(100, Math.round(this.spent(phase) / budget * 100));
    },
    canDeleteEntry(entry) {
      if (!this.isOpen) {
        return false;
      }
      return (this.currentUser && entry.user_id === this.currentUser.id && this.access.log_time) || this.access.edit_budget;
    },
    loadEntries() {
      let self = this;
      piincHttp.get('projects/' + this.project.slug + '/engagement/time-entries').then(function(response) {
        self.entries = response.data;
      }, function() {});
    },
    toggleEntries() {
      this.showEntries = !this.showEntries;
      if (this.showEntries) {
        this.loadEntries();
      }
    },
    showEditModal() {
      let self = this;
      this.error = '';
      this.budgetForm = {};
      _.forEach(this.phases, phase => {
        self.$set(self.budgetForm, phase, self.budget(phase) || '');
      });
      this.$refs['edit-budget-modal'].show();
    },
    saveBudget() {
      let self = this;
      this.buttonDisabled = true;
      piincHttp.put('projects/' + this.project.slug + '/engagement/budget', {
        phases: this.budgetForm
      }).then(function(response) {
        self.buttonDisabled = false;
        self.$refs['edit-budget-modal'].hide();
        self.$toastr('success', self.$t('projects.engagement.budget.save_success'));
        self.$emit('changed', response.data);
      }, function(error) {
        self.buttonDisabled = false;
        self.error = error.data && error.data.message ? error.data.message : '';
      });
    },
    showLogModal() {
      this.error = '';
      this.logForm = this.emptyLogForm();
      this.$refs['log-time-modal'].show();
    },
    logTime() {
      let self = this;
      const hours = Number(this.logForm.hours);
      if (!hours || hours < 0.25 || hours > 24) {
        this.error = this.$t('projects.engagement.budget.hours_error');
        return;
      }

      this.error = '';
      this.buttonDisabled = true;
      piincHttp.post('projects/' + this.project.slug + '/engagement/time-entries', this.logForm).then(function() {
        self.buttonDisabled = false;
        self.$refs['log-time-modal'].hide();
        self.$toastr('success', self.$t('projects.engagement.budget.log_success'));
        self.$emit('reload');
        if (self.showEntries) {
          self.loadEntries();
        }
      }, function(error) {
        self.buttonDisabled = false;
        self.error = error.data && error.data.message ? error.data.message : '';
      });
    },
    deleteEntry(entry) {
      let self = this;
      piincHttp.delete('projects/' + this.project.slug + '/engagement/time-entries/' + entry.id).then(function() {
        self.$toastr('success', self.$t('projects.engagement.budget.delete_success'));
        self.$emit('reload');
        self.loadEntries();
      }, function(error) {
        self.$toastr('error', error.data && error.data.message ? error.data.message : '');
      });
    }
  }
}
</script>
